import type { Database } from './database.types';

export type Tenant = Database['public']['Tables']['tenants']['Row'];
export type Membership = Database['public']['Tables']['memberships']['Row'];
export type TenantRole = Database['public']['Enums']['tenant_role'];

export const TENANT_ADMIN_ROLES = ['admin', 'owner'] as const satisfies readonly TenantRole[];

export function resolveActiveTenant(
  tenants: Tenant[],
  memberships: Membership[],
  preferredSlug: string
): Tenant | null {
  if (tenants.length === 0) {
    return null;
  }

  const membershipTenantIds = new Set(memberships.map((membership) => membership.tenant_id));
  const membershipTenants = tenants.filter((tenant) => membershipTenantIds.has(tenant.id));

  const preferredMembershipTenant = membershipTenants.find(
    (tenant) => tenant.slug === preferredSlug
  );
  if (preferredMembershipTenant) {
    return preferredMembershipTenant;
  }

  if (membershipTenants.length > 0) {
    return membershipTenants[0];
  }

  return tenants.find((tenant) => tenant.slug === preferredSlug) ?? tenants[0] ?? null;
}

export function isUserTenantOwner(
  memberships: Membership[],
  tenantId: string | null | undefined,
  userId: string | null | undefined
): boolean {
  if (!tenantId || !userId) {
    return false;
  }

  return memberships.some(
    (membership) =>
      membership.tenant_id === tenantId &&
      membership.user_id === userId &&
      membership.role === 'owner'
  );
}

export function isUserTenantAdmin(
  memberships: Membership[],
  tenantId: string | null | undefined,
  userId: string | null | undefined
): boolean {
  if (!tenantId || !userId) {
    return false;
  }

  return memberships.some(
    (membership) =>
      membership.tenant_id === tenantId &&
      membership.user_id === userId &&
      TENANT_ADMIN_ROLES.includes(membership.role as (typeof TENANT_ADMIN_ROLES)[number])
  );
}

export function hasTenantAdminMembership(memberships: Membership[]): boolean {
  return memberships.some((membership) =>
    TENANT_ADMIN_ROLES.includes(membership.role as (typeof TENANT_ADMIN_ROLES)[number])
  );
}

export function adminTenantsForUser(
  tenants: Tenant[],
  memberships: Membership[],
  isGlobalAdmin = false
): Tenant[] {
  if (isGlobalAdmin) {
    return tenants;
  }

  return tenants.filter((tenant) => {
    const role = memberships.find((membership) => membership.tenant_id === tenant.id)?.role;
    return TENANT_ADMIN_ROLES.includes(role as (typeof TENANT_ADMIN_ROLES)[number]);
  });
}

export function memberTenantsForUser(
  tenants: Tenant[],
  memberships: Membership[],
  isGlobalAdmin = false
): Tenant[] {
  if (isGlobalAdmin) {
    return tenants;
  }

  const memberTenantIds = new Set(memberships.map((membership) => membership.tenant_id));
  return tenants.filter((tenant) => memberTenantIds.has(tenant.id));
}

export function getMembershipRole(
  memberships: Membership[],
  tenantId: string | null | undefined,
  userId: string | null | undefined
): TenantRole | null {
  if (!tenantId || !userId) {
    return null;
  }

  return (
    memberships.find(
      (membership) => membership.tenant_id === tenantId && membership.user_id === userId
    )?.role ?? null
  );
}
